import React from 'react';
import { Box, Chip, Container, Grid, Paper, Typography } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { motion } from 'framer-motion';
import SkillsItems from './SkillsItems';
import skillsConfig from '../../assets/configs/skillsConfig';
import { useLang } from '../../utils/i18n';

const UI = {
  en: {
    main: 'Main Skills & Tools',
    complementary: 'Complementary Skills & Tools',
    languages: 'Languages',
    certifications: 'Certifications',
  },
  es: {
    main: 'Habilidades y herramientas principales',
    complementary: 'Habilidades y herramientas complementarias',
    languages: 'Idiomas',
    certifications: 'Certificaciones',
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const levelColor = (level) => {
  if (level === 'Native' || level === 'Nativo') return { bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.35)', color: '#22c55e' };
  if (level === 'C2') return { bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.35)', color: '#22c55e' };
  if (level === 'C1') return { bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.35)', color: '#22c55e' };
  if (level === 'B2') return { bg: 'rgba(125,211,252,0.1)', border: 'rgba(125,211,252,0.3)', color: '#7dd3fc' };
  if (level === 'B1') return { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', color: '#F59E0B' };
  return { bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.2)', color: '#94a3b8' };
};

const LanguageCard = ({ lang, langCode }) => {
  const name = langCode === 'es' ? lang.nameEs : lang.name;
  const colors = levelColor(lang.level);

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 3,
        bgcolor: 'background.paper',
        transition: 'border-color .2s ease, transform .2s ease',
        '&:hover': {
          borderColor: 'rgba(6,182,212,0.25)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        component="img"
        src={lang.flag}
        alt={name}
        sx={{
          width: 44,
          height: 30,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
        }}
      />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
          {name}
        </Typography>
      </Box>
      <Chip
        label={lang.level}
        size="small"
        sx={{
          bgcolor: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.color,
          fontWeight: 700,
          fontSize: 11,
          flexShrink: 0,
        }}
      />
    </Paper>
  );
};

const CertCard = ({ cert }) => (
  <Paper
    elevation={0}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 3,
      bgcolor: 'background.paper',
      transition: 'border-color .2s ease, transform .2s ease',
      '&:hover': { borderColor: 'rgba(139,92,246,0.35)', transform: 'translateY(-2px)' },
    }}
  >
    <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      {cert.icon}
      <WorkspacePremiumIcon sx={{ fontSize: 16, color: '#a78bfa', opacity: 0.6 }} />
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.3 }}>
        {cert.code}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
        {cert.name}
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', display: 'block' }}>
        {cert.issuer}
      </Typography>
    </Box>
    <Chip
      label={cert.date}
      size="small"
      sx={{
        bgcolor: 'rgba(139,92,246,0.1)',
        border: '1px solid rgba(139,92,246,0.3)',
        color: '#a78bfa',
        fontWeight: 700,
        fontSize: 11,
        flexShrink: 0,
      }}
    />
  </Paper>
);

const Section = ({ title, children, delay = 0 }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: '-60px' }}
    variants={{
      hidden: { opacity: 0, y: 24 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay, ease: [0.25, 0.46, 0.45, 0.94] } },
    }}
  >
    <Box
      sx={(th) => ({
        textAlign: 'center',
        py: 5,
        px: 3,
        backgroundColor: 'background.paper',
        borderRadius: 4,
        border: '1px solid rgba(255,255,255,0.07)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      })}
    >
      <Typography
        variant="h3"
        component="h2"
        sx={{ color: 'text.primary', pb: 3, fontWeight: 900, letterSpacing: -0.5 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  </motion.div>
);

export default function Skills() {
  const [lang] = useLang();
  const t = UI[lang] || UI.en;

  return (
    <section id="skills">
      <Container maxWidth="lg" sx={{ pt: 6, pb: 4 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={fadeUp}
        >
          <Box
            sx={{
              py: 5,
              px: 3,
              backgroundColor: 'background.paper',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.07)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{ color: 'text.primary', pb: 4, fontWeight: 900, letterSpacing: -0.5, textAlign: 'center' }}
            >
              {t.certifications}
            </Typography>
            <Grid container spacing={2} sx={{ maxWidth: 900, mx: 'auto' }}>
              {skillsConfig.certifications.map((c) => (
                <Grid item xs={12} sm={6} key={c.id}>
                  <CertCard cert={c} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        <Section title={t.main} delay={0}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <SkillsItems config={skillsConfig.mainSkills} />
          </Box>
        </Section>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        <Section title={t.complementary} delay={0.1}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <SkillsItems config={skillsConfig.complementarySkills} />
          </Box>
        </Section>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={fadeUp}
        >
          <Box
            sx={{
              py: 5,
              px: 3,
              backgroundColor: 'background.paper',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.07)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{ color: 'text.primary', pb: 4, fontWeight: 900, letterSpacing: -0.5, textAlign: 'center' }}
            >
              {t.languages}
            </Typography>
            <Grid container spacing={2} sx={{ maxWidth: 900, mx: 'auto' }}>
              {skillsConfig.languages.map((l) => (
                <Grid item xs={12} sm={6} md={4} key={l.id}>
                  <LanguageCard lang={l} langCode={lang} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </Container>

    </section>
  );
}
